import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Lock, Smartphone, LogOut, Loader2 } from "lucide-react";
import { useWaiterWorkSession } from "@/hooks/useWaiterWorkSession";
import { useAuth } from "@/hooks/useAuth";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { InstallButton } from "@/components/pwa/InstallButton";
interface WaiterCheckinScreenProps {
  onCheckinSuccess?: () => void;
}

export function WaiterCheckinScreen({ onCheckinSuccess }: WaiterCheckinScreenProps) {
  const { user, signOut } = useAuth();
  const { activeRestaurant } = useRestaurantContext();
  const { checkinWithCode, requestApproval, isPending, cancelPendingRequest, hasActiveSession } =
    useWaiterWorkSession();
  
  const [code, setCode] = useState("");

  // Auto-navigate when session becomes active via realtime approval
  useEffect(() => {
    if (hasActiveSession) {
      onCheckinSuccess?.();
    }
  }, [hasActiveSession, onCheckinSuccess]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || code.length < 6) return;

    setIsSubmitting(true);
    const success = await checkinWithCode(code.trim());
    setIsSubmitting(false);

    if (success) {
      onCheckinSuccess?.();
    }
  };

  const handleRequestApproval = async () => {
    setIsSubmitting(true);
    await requestApproval();
    setIsSubmitting(false);
  };

  const handleCancelRequest = async () => {
    setIsSubmitting(true);
    await cancelPendingRequest();
    setIsSubmitting(false);
  };

  const handleSignOut = async () => {
    await signOut();
  };

  // Tela de aguardando aprovação
  if (isPending) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-amber-600 animate-spin" />
            </div>
            <CardTitle className="text-xl">Aguardando Aprovação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-center text-muted-foreground">
              Sua solicitação foi enviada para o PDV. Aguarde a aprovação do
              gerente ou operador de caixa.
            </p>

            <div className="flex justify-center">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                </span>
                Aguardando...
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={handleCancelRequest}
              disabled={isSubmitting}
            >
              Cancelar Solicitação
            </Button>

            <Separator />

            <div className="text-center text-sm text-muted-foreground">
              <p>👤 {user?.email}</p>
              <p>📍 {activeRestaurant?.name}</p>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="w-full text-muted-foreground"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de check-in
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-xl">Check-in Necessário</CardTitle>
          <p className="text-muted-foreground text-sm mt-2">
            Para lançar pedidos, faça o check-in da sua sessão de trabalho
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Opção 1: Código do dia */}
          <form onSubmit={handleCodeSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Código do Dia</label>
              <Input
                type="text"
                placeholder="Digite o código de 6 caracteres"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={6}
                className="text-center text-2xl tracking-widest font-mono uppercase"
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground text-center">
                Peça o código ao gerente ou operador de caixa
              </p>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={code.length < 6 || isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Validar Código
            </Button>
          </form>

          <div className="relative">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
              ou
            </span>
          </div>

          {/* Opção 2: Solicitar aprovação */}
          <Button
            variant="outline"
            className="w-full"
            onClick={handleRequestApproval}
            disabled={isSubmitting}
          >
            <Smartphone className="h-4 w-4 mr-2" />
            Solicitar Liberação ao PDV
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            O gerente receberá uma notificação para aprovar seu acesso
          </p>

          <Separator />

          {/* Informações do usuário */}
          <div className="text-center text-sm text-muted-foreground space-y-1">
            <p>👤 {user?.email}</p>
            <p>📍 {activeRestaurant?.name}</p>
          </div>

          {/* Install button - only visible when PWA installation is available */}
          <InstallButton variant="subtle" className="w-full" />

          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            onClick={handleSignOut}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sair
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
